import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * PageHeader — unified page title block.
 *
 * Layout (RTL):
 *   [breadcrumb]
 *   [title · subtitle]                      [actions]
 *   ───────────────────────────────────────────────── (divider)
 *
 * Props:
 *   title        — string, required (Cairo Bold 24–28px)
 *   subtitle     — string, optional (neutral-500, 14px)
 *   breadcrumbs  — [{ label, to }] optional; "الرئيسية" auto-prepended
 *   icon         — Lucide icon component, optional
 *   actions      — ReactNode, rendered on the start side (primary/ghost buttons)
 *   extra        — ReactNode, rendered under the title (filters, search bar, etc.)
 *   divider      — boolean, default true
 */
const PageHeader = ({
  title,
  subtitle = '',
  breadcrumbs = [],
  icon: Icon = null,
  actions = null,
  extra = null,
  divider = true,
}) => {
  const location = useLocation();

  const trail = [
    { label: 'الرئيسية', to: '/dashboard', icon: Home },
    ...breadcrumbs,
    { label: title, to: location.pathname, current: true },
  ];

  return (
    <div className="mb-6">
      {/* Breadcrumb */}
      <nav
        aria-label="breadcrumb"
        className="flex items-center gap-1.5 text-xs text-neutral-500 dark:text-neutral-400 mb-3"
      >
        {trail.map((c, i) => {
          const BcIcon = c.icon;
          const isLast = i === trail.length - 1;
          return (
            <React.Fragment key={`${c.to}-${i}`}>
              {i > 0 && <ChevronRight size={12} className="opacity-50 rtl:rotate-180" aria-hidden="true" />}
              {isLast ? (
                <span aria-current="page" className="font-medium text-neutral-700 dark:text-neutral-200 truncate max-w-[240px]">
                  {c.label}
                </span>
              ) : (
                <Link
                  to={c.to}
                  className="flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                  {BcIcon && <BcIcon size={12} aria-hidden="true" />}
                  <span className="truncate max-w-[180px]">{c.label}</span>
                </Link>
              )}
            </React.Fragment>
          );
        })}
      </nav>

      {/* Title row */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          {Icon && (
            <span
              className="mt-1 flex-shrink-0 flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-300"
              aria-hidden="true"
            >
              <Icon size={20} />
            </span>
          )}
          <div className="min-w-0">
            <h1 className="font-arabic text-2xl md:text-[28px] font-bold leading-tight text-neutral-900 dark:text-neutral-50 tracking-tight">
              {title}
            </h1>
            {subtitle && (
              <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed max-w-2xl">
                {subtitle}
              </p>
            )}
          </div>
        </div>

        {actions && (
          <div className="flex items-center gap-2 flex-shrink-0">
            {actions}
          </div>
        )}
      </div>

      {extra && <div className="mt-4">{extra}</div>}

      {divider && (
        <div className={cn('mt-5 border-b border-neutral-200 dark:border-neutral-800')} />
      )}
    </div>
  );
};

export default PageHeader;
